import { isArray, isEmpty, isNil, isString, omitBy } from 'lodash';

import graphqlClient from '../graphql';
import {
  bringBackNonRoutableRequestToPomGql,
  cancelNonRoutableRequestGql,
  cancelRelayFaxGql,
  getNonRoutableRelayFaxDetailGql,
  getNonRoutableRequestGql,
  listNonRoutableRequestsGql,
  moveRelayFaxOrderToPomGql,
  reconcileRelayFaxGql,
  resendRelayFaxGql,
  retryNonRoutableRequestRoutingGql,
} from './gql';

// The backend caps `limit` at 200 and `offset` at 10000. 25 matches every other work queue.
export const NON_ROUTABLE_REQUESTS_PAGE_SIZE = 25;

// parkedAt DESC is the only index-native ordering, and WHICH index serves it depends on the
// status filter: idx_non_routable_request_queue for the default view, idx_non_routable_request_
// cancelled when Cancelled is the only status ticked, and NEITHER for a mixed selection, which
// plans as a scan. All three are (created_timestamp DESC, id DESC). The other sort keys sort
// correctly and cost a server-side sort, which is fine for an operator who asks for them but is
// not something to make the default.
export const NON_ROUTABLE_REQUESTS_DEFAULT_SORT = { sortKey: 'parkedAt', sortOrder: 'DESC' };

/**
 * AN EMPTY ARRAY IS NOT "NO FILTER" — it reads as "exclude everything". A multi-select the
 * operator has cleared must send NO key at all, or the queue serves zero rows and the screen says
 * "nothing is parked" while prescriptions sit in the table unseen. The backend already had to be
 * fixed for this; do not recreate it here.
 *
 * Also drops null / undefined and blank strings, so a cleared text box does not become
 * `referenceId: ""` and match nothing.
 */
export const sanitizeNonRoutableRequestsFilter = (filter) =>
  omitBy(
    filter,
    (value) =>
      isNil(value) ||
      (isArray(value) && isEmpty(value)) ||
      (isString(value) && isEmpty(value.trim()))
  );

export const listNonRoutableRequestsApi = ({
  page = 1,
  limit = NON_ROUTABLE_REQUESTS_PAGE_SIZE,
  filter,
  sortBy = NON_ROUTABLE_REQUESTS_DEFAULT_SORT,
} = {}) => {
  const offset = (page - 1) * limit;
  const sanitized = sanitizeNonRoutableRequestsFilter(filter);

  return graphqlClient
    .request(listNonRoutableRequestsGql, {
      limit,
      offset,
      // An entirely empty filter goes as `undefined`, not `{}` — same reason as above.
      filter: isEmpty(sanitized) ? undefined : sanitized,
      sortBy,
    })
    .then(({ nonRoutableRequests }) => nonRoutableRequests);
};

// Resolves `null` when no request carries that id. That is an answer, not a failure — the field
// is nullable on the backend precisely so a primary-key miss does not read as a fault.
export const getNonRoutableRequestApi = ({ nonRoutableRequestId }) =>
  graphqlClient
    .request(getNonRoutableRequestGql, { nonRoutableRequestId })
    .then(({ nonRoutableRequest }) => nonRoutableRequest);

// IRREVERSIBLE. Releases the parked request into the normal POM flow, which creates an order that
// will be dispensed. Resolves with the row as the database now has it.
export const bringBackNonRoutableRequestToPomApi = ({ nonRoutableRequestId }) =>
  graphqlClient
    .request(bringBackNonRoutableRequestToPomGql, { nonRoutableRequestId })
    .then(({ bringBackNonRoutableRequestToPom }) => bringBackNonRoutableRequestToPom);

// IRREVERSIBLE in the opposite direction. The prescription is never filled; the partner is sent
// the ORDER_CANCELED webhook carrying `cancelReason`.
export const cancelNonRoutableRequestApi = ({ nonRoutableRequestId, cancelReason }) =>
  graphqlClient
    .request(cancelNonRoutableRequestGql, { nonRoutableRequestId, cancelReason })
    .then(({ cancelNonRoutableRequest }) => cancelNonRoutableRequest);

// RE-RUNS ROUTING FROM THE FIRST STEP against the current configuration, re-invoking the Routing
// Hub rather than trusting the decision stored at intake. Resolves with the row as the database now
// has it — which may still be parked, if routing reached the same answer.
export const retryNonRoutableRequestRoutingApi = ({ nonRoutableRequestId }) =>
  graphqlClient
    .request(retryNonRoutableRequestRoutingGql, { nonRoutableRequestId })
    .then(({ retryNonRoutableRequestRouting }) => retryNonRoutableRequestRouting);

// ---- POM-6279: parked RELAY FAX -------------------------------------------------------------

// Resolves `null` when the id is not a parked relay fax — an answer, not a failure.
export const getNonRoutableRelayFaxDetailApi = ({ nonRoutableRequestId }) =>
  graphqlClient
    .request(getNonRoutableRelayFaxDetailGql, { nonRoutableRequestId })
    .then(({ nonRoutableRelayFaxDetail }) => nonRoutableRelayFaxDetail);

// Resolves the RelayFaxActionResult `{ request, outcome, faxMayHaveBeenSent }`.
//
// `confirmResend` is sent ONLY when true. Omitting it is what the server reads as "not confirmed",
// and the first click must never carry a confirmation the operator has not given.
export const resendRelayFaxApi = ({ nonRoutableRequestId, confirmResend = false }) =>
  graphqlClient
    .request(resendRelayFaxGql, {
      nonRoutableRequestId,
      ...(confirmResend === true ? { confirmResend: true } : {}),
    })
    .then(({ resendRelayFax }) => resendRelayFax);

export const reconcileRelayFaxApi = ({ nonRoutableRequestId }) =>
  graphqlClient
    .request(reconcileRelayFaxGql, { nonRoutableRequestId })
    .then(({ reconcileRelayFax }) => reconcileRelayFax);

export const cancelRelayFaxApi = ({ nonRoutableRequestId }) =>
  graphqlClient
    .request(cancelRelayFaxGql, { nonRoutableRequestId })
    .then(({ cancelRelayFax }) => cancelRelayFax);

export const moveRelayFaxOrderToPomApi = ({ nonRoutableRequestId }) =>
  graphqlClient
    .request(moveRelayFaxOrderToPomGql, { nonRoutableRequestId })
    .then(({ moveRelayFaxOrderToPom }) => moveRelayFaxOrderToPom);
